import type { QueryClient } from '@tanstack/react-query';
import { refreshDetailsViews } from '../../features/details/freshness';
import { fetchStudyHead, studyHeadKey } from './studyHead';

/**
 * Every view built from the catalogue, by the first element of its query
 * key. A change anywhere (an edit, a like, a comment, a finished import, a
 * delete) refreshes ALL of them at once, so no page shows yesterday's copy of
 * something that just changed. Adding a catalogue view means adding its key
 * here, and nowhere else.
 */
export const CATALOGUE_KEYS = [
  'collections',
  'collection',
  'collection-targets',
  'explore',
  'public-collection',
  'home',
  'search',
  'study-head',
  'share-study-head',
  'share-collection',
  'usage',
] as const;

/** This person's uploads, the top bar's list (GET /uploads/active): changed by uploads alone, so not a catalogue view. */
export const UPLOAD_LIST_KEY = ['upload-list'] as const;

export function refreshUploadList(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: UPLOAD_LIST_KEY });
}

export function invalidateCatalogue(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ predicate: (q) => (CATALOGUE_KEYS as readonly string[]).includes(q.queryKey[0] as string) });
}

type HeadUid = { study_instance_uid?: string } | null;

/**
 * An image of a study was just published. The catalogue views list it, and so
 * does the study itself in the viewer — whose image list the app otherwise keeps
 * for minutes, so "Open study" after adding images showed the study without
 * them. Only that study's viewer reads are refreshed, found by its UID: from its
 * head, read once when not at hand (a study this upload made was never opened)
 * and kept like the page's own. Only when the head cannot be read at all is
 * every study's refreshed, rather than keep this one's.
 */
export async function refreshPublished(
  qc: QueryClient,
  studyId: string | null,
  readHead: (studyId: string) => Promise<HeadUid> = (id) => fetchStudyHead<HeadUid>(id),
): Promise<void> {
  void refreshUploadList(qc);
  let uid: string | undefined;
  if (studyId) {
    const head = await qc.ensureQueryData({ queryKey: studyHeadKey(studyId), queryFn: () => readHead(studyId) }).catch(() => null);
    uid = (head as HeadUid)?.study_instance_uid;
  }
  await refreshDetailsViews(qc, CATALOGUE_KEYS, uid);
}

/**
 * The app's query client keeps results fresh for five minutes, which is right
 * for DICOM (an object never changes) and wrong for the community catalogue
 * (it changes all the time, often from another person). Catalogue views and
 * threads reload whenever a page that shows them is opened; images keep the
 * long cache.
 */
export function applyCommunityQueryDefaults(qc: QueryClient): void {
  for (const k of [...CATALOGUE_KEYS, ...UPLOAD_LIST_KEY, 'comments', 'members', 'profile']) qc.setQueryDefaults([k], { staleTime: 0 });
}
